import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, SlidersHorizontal, Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (url.startsWith('/uploads/')) {
    return `${backendUrl}/api${url}`;
  }
  return `${backendUrl}${url}`;
};

export default function AdminAntesAhora() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [mediaSelectMode, setMediaSelectMode] = useState("before"); // "before" or "after"
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    before_image_id: "",
    before_image_url: "",
    after_image_id: "",
    after_image_url: "",
    year_before: "",
    year_after: "",
    location: "",
    status: "draft",
  });

  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchItems();
  }, [page, limit]);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/before-after?skip=${(page - 1) * limit}&limit=${limit}`, { headers });
      setItems(response.data.items);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media?limit=100`, { headers });
      setMediaList(response.data.items);
    } catch (error) {
      console.error("Error fetching media:", error);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        title: item.title || "",
        description: item.description || "",
        before_image_id: item.before_image_id || "",
        before_image_url: item.before_image_url || "",
        after_image_id: item.after_image_id || "",
        after_image_url: item.after_image_url || "",
        year_before: item.year_before || "",
        year_after: item.year_after || "",
        location: item.location || "",
        status: item.status || "draft",
      });
    } else {
      setSelectedItem(null);
      setFormData({
        title: "",
        description: "",
        before_image_id: "",
        before_image_url: "",
        after_image_id: "",
        after_image_url: "",
        year_before: "",
        year_after: "",
        location: "",
        status: "draft",
      });
    }
    setDialogOpen(true);
  };

  const handleOpenMediaDialog = (mode) => {
    setMediaSelectMode(mode);
    fetchMedia();
    setMediaDialogOpen(true);
  };

  const handleMediaSelect = (media) => {
    let imageUrl = media.url;
    if (imageUrl && imageUrl.startsWith('/uploads/')) {
      imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api${imageUrl}`;
    }

    if (mediaSelectMode === "before") {
      setFormData({
        ...formData,
        before_image_id: media.media_id,
        before_image_url: imageUrl,
      });
    } else {
      setFormData({
        ...formData,
        after_image_id: media.media_id,
        after_image_url: imageUrl,
      });
    }
    setMediaDialogOpen(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Solo se permiten imágenes");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('title', file.name.replace(/\.[^/.]+$/, ''));

      const response = await axios.post(`${API}/media/upload`, formDataUpload, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Imagen subida");
      await fetchMedia();
      handleMediaSelect(response.data);
    } catch (error) {
      toast.error("Error al subir imagen");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error("El título es requerido");
      return;
    }
    if (!formData.before_image_id || !formData.after_image_id) {
      toast.error("Debes seleccionar ambas imágenes");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description || null,
        before_image_id: formData.before_image_id,
        after_image_id: formData.after_image_id,
        year_before: formData.year_before || null,
        year_after: formData.year_after || null,
        location: formData.location || null,
        status: formData.status,
      };

      if (selectedItem) {
        await axios.put(`${API}/before-after/${selectedItem.item_id}`, payload, { headers });
        toast.success("Comparación actualizada");
      } else {
        await axios.post(`${API}/before-after`, payload, { headers });
        toast.success("Comparación creada");
      }

      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      await axios.delete(`${API}/before-after/${selectedItem.item_id}`, { headers });
      toast.success("Comparación eliminada");
      setDeleteDialogOpen(false);
      fetchItems();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-text">Antes y Ahora</h1>
            <p className="text-brand-textSecondary">Comparaciones fotográficas históricas</p>
          </div>
          <Button onClick={() => handleOpenDialog()} data-testid="new-before-after-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nueva Comparación
          </Button>
        </div>

        {/* Items List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="aspect-video bg-gray-200 rounded-lg mb-3" />
                <div className="h-5 bg-gray-200 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : items.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {items.map((item) => (
              <div
                key={item.item_id}
                className="bg-white rounded-xl overflow-hidden shadow-sm border"
              >
                <div className="aspect-video bg-gray-100 relative flex">
                  {/* Before */}
                  <div className="w-1/2 relative">
                    {item.before_image_url ? (
                      <img
                        src={getImageUrl(item.before_image_url)}
                        alt="Antes"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400">Antes</span>
                      </div>
                    )}
                    <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                      {item.year_before || "Antes"}
                    </div>
                  </div>
                  {/* Divider */}
                  <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-white z-10" />
                  {/* After */}
                  <div className="w-1/2 relative">
                    {item.after_image_url ? (
                      <img
                        src={getImageUrl(item.after_image_url)}
                        alt="Ahora"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400">Ahora</span>
                      </div>
                    )}
                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                      {item.year_after || "Ahora"}
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-brand-text">{item.title}</h3>
                      <p className="text-sm text-brand-textMuted">
                        {item.status === "published" ? "Publicada" : "Borrador"}
                        {item.location && ` • ${item.location}`}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(item)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedItem(item);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl">
            <SlidersHorizontal className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay comparaciones creadas</p>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedItem ? "Editar Comparación" : "Nueva Comparación"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ej: Puente de Choluteca"
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Historia o contexto de la comparación"
                rows={3}
              />
            </div>

            {/* Before/After Images */}
            <div className="grid grid-cols-2 gap-4">
              {/* Before */}
              <div>
                <Label>Foto Antes *</Label>
                {formData.before_image_url ? (
                  <div className="relative mt-2">
                    <img
                      src={getImageUrl(formData.before_image_url)}
                      alt="Antes"
                      className="w-full aspect-video object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="absolute bottom-2 right-2"
                      onClick={() => handleOpenMediaDialog("before")}
                    >
                      Cambiar
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-2 aspect-video flex-col"
                    onClick={() => handleOpenMediaDialog("before")}
                  >
                    <SlidersHorizontal className="h-8 w-8 text-gray-400 mb-2" />
                    Seleccionar ANTES
                  </Button>
                )}
                <Input
                  value={formData.year_before}
                  onChange={(e) => setFormData({ ...formData, year_before: e.target.value })}
                  placeholder="Año (ej: 1980)"
                  className="mt-2"
                />
              </div>

              {/* After */}
              <div>
                <Label>Foto Ahora *</Label>
                {formData.after_image_url ? (
                  <div className="relative mt-2">
                    <img
                      src={getImageUrl(formData.after_image_url)}
                      alt="Ahora"
                      className="w-full aspect-video object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="absolute bottom-2 right-2"
                      onClick={() => handleOpenMediaDialog("after")}
                    >
                      Cambiar
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-2 aspect-video flex-col"
                    onClick={() => handleOpenMediaDialog("after")}
                  >
                    <SlidersHorizontal className="h-8 w-8 text-gray-400 mb-2" />
                    Seleccionar AHORA
                  </Button>
                )}
                <Input
                  value={formData.year_after}
                  onChange={(e) => setFormData({ ...formData, year_after: e.target.value })}
                  placeholder="Año (ej: 2024)"
                  className="mt-2"
                />
              </div>
            </div>

            <div>
              <Label>Ubicación</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Ej: Centro de Choluteca"
              />
            </div>

            <div>
              <Label>Estado</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="published">Publicada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Media Selection Dialog */}
      <Dialog open={mediaDialogOpen} onOpenChange={setMediaDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Seleccionar imagen {mediaSelectMode === "before" ? "ANTES" : "AHORA"}
            </DialogTitle>
          </DialogHeader>

          {/* Upload */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              id="ba-upload"
            />
            <label htmlFor="ba-upload" className="cursor-pointer">
              {uploading ? (
                <Loader2 className="h-8 w-8 mx-auto text-primary animate-spin" />
              ) : (
                <>
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Subir nueva imagen</span>
                </>
              )}
            </label>
          </div>

          {/* Media Grid */}
          <div className="grid grid-cols-4 gap-3">
            {mediaList.map((media) => (
              <button
                key={media.media_id}
                type="button"
                onClick={() => handleMediaSelect(media)}
                className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-primary transition-colors"
              >
                <img
                  src={getImageUrl(media.url)}
                  alt={media.title}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar comparación?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="antes-ahora-pagination"
          label="registros"
        />
    </AdminLayout>
  );
}
